import { EConfiguratorQuestionType, EProjectScope, IConfiguratorSection } from '../models/configurator.model';
import {
  configuratorIcon,
  BATHROOM_EXTRA_OPTIONS,
  BATHTUB_FITTINGS_OPTIONS,
  BATHTUB_OPTIONS,
  BATHTUB_TYPES_WITH_FITTINGS,
  CEILING_OPTIONS,
  ELECTRICAL_WISH_OPTIONS,
  ELECTRICITY_TYPE_OPTIONS,
  ELECTRICITY_TYPES_WITH_WISHES,
  ENERGY_SOURCE_OPTIONS,
  FITTINGS_OPTIONS,
  FLOOR_CURRENT_OPTIONS,
  FLOOR_WANTED_OPTIONS,
  GUEST_WC_BASIN_OPTIONS,
  GUEST_WC_TOILET_OPTIONS,
  GUEST_WC_WALL_OPTIONS,
  HEATING_LOCATION_OPTIONS,
  HEATING_PLAN_OPTIONS,
  HEATING_TYPE_OPTIONS,
  INTERIOR_DOOR_ITEMS,
  KITCHEN_SUPPLIER_OPTIONS,
  PLANNING_SERVICE_OPTIONS,
  ROOF_AGE_OPTIONS,
  ROOM_ITEMS,
  SHOWER_OPTIONS,
  SHOWER_TYPES_WITH_FITTINGS,
  TOILET_OPTIONS,
  UNKNOWN_VALUE,
  WALL_TILES_OPTIONS,
  WALLPAPER_OPTIONS,
  WASHBASIN_OPTIONS,
  WINDOW_ELEMENT_ITEMS,
  WINDOWS_REPLACE_OPTIONS,
  WINDOWS_REPLACED,
  YES_NO_UNKNOWN_OPTIONS,
  NONE_VALUE
} from './configurator-options.data';
import {
  BATHROOMS_KEY,
  ERoom,
  getBathroomCount,
  getGuestWcCount,
  hasExteriorWork,
  hasKitchen,
  hasTrade,
  isCompleteRenovation,
  isLivingRenovation,
  isNotScope,
  isScope
} from './configurator-predicates';
import { getBathroomNames, getBathroomSummaryName } from '../helpers/configurator-bathroom.helper';

const T = 'ProjectConfiguratorV2.Questions';
const S = 'ProjectConfiguratorV2.Sections';

/** Step 3 "Umfang". No required questions: whatever is unknown is clarified on site. */
export const WORKS_STEP_SECTIONS: IConfiguratorSection[] = [
  {
    id: 'rooms',
    icon: configuratorIcon('grid'),
    title: `${S}.rooms`,
    show: isLivingRenovation,
    questions: [
      {
        id: 'rooms',
        type: EConfiguratorQuestionType.COUNTERS,
        summaryLabel: `${T}.rooms.summary`,
        label: `${T}.rooms.label`,
        items: ROOM_ITEMS,
        summaryItemNames: (answers) => ({ [ERoom.BATHROOM]: getBathroomNames(answers) }),
        allowCustom: true,
        customLabel: `${T}.rooms.customLabel`
      }
    ]
  },
  {
    // One section per bathroom; title, name and removal are rendered by configurator-bathroom-header.
    id: 'bathroom',
    icon: configuratorIcon('bath'),
    repeat: { key: BATHROOMS_KEY, count: getBathroomCount },
    summaryPrefix: (answers, index, translate) => `${getBathroomSummaryName(answers, index, translate)} · `,
    questions: [
      {
        id: 'size',
        type: EConfiguratorQuestionType.NUMBER,
        summaryLabel: `${T}.bathroomSize.summary`,
        label: `${T}.bathroomSize.label`,
        suffix: 'm²',
        step: 0.5,
        seed: 6,
        half: true
      },
      {
        id: 'shower',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.shower.label`,
        options: SHOWER_OPTIONS,
        allowCustom: true
      },
      {
        id: 'showerFittings',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.showerFittings.summary`,
        label: `${T}.showerFittings.label`,
        options: FITTINGS_OPTIONS,
        allowCustom: true,
        show: (_, item) => SHOWER_TYPES_WITH_FITTINGS.includes(item?.shower as string)
      },
      {
        id: 'bathtub',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.bathtub.label`,
        options: BATHTUB_OPTIONS,
        allowCustom: true
      },
      {
        id: 'bathtubFittings',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.bathtubFittings.summary`,
        label: `${T}.bathtubFittings.label`,
        options: BATHTUB_FITTINGS_OPTIONS,
        allowCustom: true,
        show: (_, item) => BATHTUB_TYPES_WITH_FITTINGS.includes(item?.bathtub as string)
      },
      {
        id: 'washbasin',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.washbasin.label`,
        options: WASHBASIN_OPTIONS,
        allowCustom: true
      },
      {
        id: 'washbasinFittings',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.washbasinFittings.summary`,
        label: `${T}.washbasinFittings.label`,
        options: FITTINGS_OPTIONS,
        allowCustom: true,
        show: (_, item) => !!item?.washbasin
      },
      {
        id: 'toilet',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.toilet.label`,
        options: TOILET_OPTIONS,
        allowCustom: true
      },
      {
        id: 'extras',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.bathroomExtras.summary`,
        label: `${T}.bathroomExtras.label`,
        options: BATHROOM_EXTRA_OPTIONS,
        multiple: true,
        allowCustom: true
      },
      {
        id: 'wallTiles',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.wallTiles.label`,
        options: WALL_TILES_OPTIONS,
        allowCustom: true
      }
    ]
  },
  {
    id: 'guestWc',
    icon: configuratorIcon('bath'),
    title: `${S}.guestWc`,
    titleParams: (answers) => ({
      count: getGuestWcCount(answers) > 1 ? ` (${getGuestWcCount(answers)}×)` : ''
    }),
    show: (answers) => getGuestWcCount(answers) > 0,
    questions: [
      {
        id: 'guestWc.size',
        type: EConfiguratorQuestionType.NUMBER,
        summaryLabel: `${T}.guestWcSize.summary`,
        label: `${T}.guestWcSize.label`,
        suffix: 'm²',
        step: 0.5,
        seed: 2,
        half: true
      },
      {
        id: 'guestWc.toilet',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.guestWcToilet.summary`,
        label: `${T}.toilet.label`,
        options: GUEST_WC_TOILET_OPTIONS,
        allowCustom: true
      },
      {
        id: 'guestWc.basin',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.guestWcBasin.summary`,
        label: `${T}.guestWcBasin.label`,
        options: GUEST_WC_BASIN_OPTIONS,
        allowCustom: true
      },
      {
        id: 'guestWc.shower',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.guestWcShower.summary`,
        label: `${T}.guestWcShower.label`,
        options: YES_NO_UNKNOWN_OPTIONS
      },
      {
        id: 'guestWc.walls',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.guestWcWalls.summary`,
        label: `${T}.wallTiles.label`,
        options: GUEST_WC_WALL_OPTIONS,
        allowCustom: true
      }
    ]
  },
  {
    id: 'kitchen',
    icon: configuratorIcon('kitchen'),
    title: `${S}.kitchen`,
    show: hasKitchen,
    questions: [
      {
        id: 'kitchenSupplier',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.kitchenSupplier.summary`,
        label: `${T}.kitchenSupplier.label`,
        help: `${T}.kitchenSupplier.help`,
        options: KITCHEN_SUPPLIER_OPTIONS,
        allowCustom: true
      },
      {
        id: 'kitchenConnectionsMove',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.kitchenConnectionsMove.summary`,
        label: `${T}.kitchenConnectionsMove.label`,
        options: YES_NO_UNKNOWN_OPTIONS
      }
    ]
  },
  {
    id: 'electrics',
    icon: configuratorIcon('zap'),
    title: `${S}.electrics`,
    show: (answers) =>
      !!answers.projectScope &&
      isNotScope(EProjectScope.EXTERIOR)(answers) &&
      (isNotScope(EProjectScope.SINGLE_WORKS)(answers) || hasTrade('electrical')(answers)),
    questions: [
      {
        id: 'electricityType',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.electricityType.label`,
        options: ELECTRICITY_TYPE_OPTIONS,
        allowCustom: true
      },
      {
        id: 'electricalWishes',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.electricalWishes.summary`,
        label: `${T}.electricalWishes.label`,
        options: ELECTRICAL_WISH_OPTIONS,
        multiple: true,
        allowCustom: true,
        show: (answers) => ELECTRICITY_TYPES_WITH_WISHES.includes(answers.electricityType)
      }
    ]
  },
  {
    id: 'heating',
    icon: configuratorIcon('flame'),
    title: `${S}.heating`,
    show: (answers) =>
      isCompleteRenovation(answers) ||
      isScope(EProjectScope.BATHROOM)(answers) ||
      hasTrade('heating')(answers),
    questions: [
      {
        id: 'heatingType',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.heatingType.summary`,
        label: `${T}.heatingType.label`,
        help: `${T}.heatingType.help`,
        options: HEATING_TYPE_OPTIONS,
        allowCustom: true
      },
      {
        id: 'heatingLocation',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.heatingLocation.summary`,
        label: `${T}.heatingLocation.label`,
        options: HEATING_LOCATION_OPTIONS,
        allowCustom: true
      },
      {
        id: 'energySource',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.energySource.summary`,
        label: `${T}.energySource.label`,
        options: ENERGY_SOURCE_OPTIONS,
        allowCustom: true
      },
      {
        id: 'heatingPlan',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.heatingPlan.summary`,
        label: `${T}.heatingPlan.label`,
        options: HEATING_PLAN_OPTIONS,
        allowCustom: true
      }
    ]
  },
  {
    id: 'walls',
    icon: configuratorIcon('wall'),
    title: `${S}.walls`,
    note: `${S}.wallsNote`,
    show: (answers) => isCompleteRenovation(answers) || hasTrade('drywall', 'walls')(answers),
    questions: [
      {
        id: 'layoutChange',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.layoutChange.summary`,
        label: `${T}.layoutChange.label`,
        help: `${T}.layoutChange.help`,
        options: YES_NO_UNKNOWN_OPTIONS
      },
      {
        id: 'wallsRemove',
        type: EConfiguratorQuestionType.NUMBER,
        label: `${T}.wallsRemove.label`,
        help: `${T}.wallsHelp`,
        suffix: 'ProjectConfiguratorV2.Units.pieces',
        half: true,
        show: (answers) => answers.layoutChange === 'yes'
      },
      {
        id: 'wallsNew',
        type: EConfiguratorQuestionType.NUMBER,
        summaryLabel: `${T}.wallsNew.summary`,
        label: `${T}.wallsNew.label`,
        help: `${T}.wallsHelp`,
        suffix: 'ProjectConfiguratorV2.Units.pieces',
        half: true,
        show: (answers) => answers.layoutChange === 'yes'
      },
      {
        id: 'wallpaper',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.wallpaper.summary`,
        label: `${T}.wallpaper.label`,
        help: `${T}.wallpaper.help`,
        options: WALLPAPER_OPTIONS
      },
      {
        id: 'ceiling',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.ceiling.label`,
        help: `${T}.ceiling.help`,
        options: CEILING_OPTIONS,
        multiple: true,
        allowCustom: true
      }
    ]
  },
  {
    id: 'floors',
    icon: configuratorIcon('floor'),
    title: `${S}.floorsCovering`,
    show: (answers) => isCompleteRenovation(answers) || hasTrade('flooring')(answers),
    questions: [
      {
        id: 'floorCurrent',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.floorCurrent.summary`,
        label: `${T}.floorCurrent.label`,
        options: FLOOR_CURRENT_OPTIONS,
        multiple: true,
        exclusiveValues: [UNKNOWN_VALUE],
        allowCustom: true
      },
      {
        id: 'floorWanted',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.floorWanted.summary`,
        label: `${T}.floorWanted.label`,
        options: FLOOR_WANTED_OPTIONS,
        multiple: true,
        exclusiveValues: [UNKNOWN_VALUE],
        allowCustom: true
      }
    ]
  },
  {
    id: 'windowsAndDoors',
    icon: configuratorIcon('door'),
    title: `${S}.windowsAndDoors`,
    show: (answers) => isCompleteRenovation(answers) || hasTrade('windows', 'doors')(answers),
    questions: [
      {
        id: 'windowsReplace',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.windowsReplace.summary`,
        label: `${T}.windowsReplace.label`,
        help: `${T}.windowsReplace.help`,
        options: WINDOWS_REPLACE_OPTIONS
      },
      {
        id: 'windowElements',
        type: EConfiguratorQuestionType.COUNTERS,
        summaryLabel: `${T}.windowElements.summary`,
        label: `${T}.windowElements.label`,
        items: WINDOW_ELEMENT_ITEMS,
        allowCustom: true,
        customLabel: `${T}.windowElements.customLabel`,
        show: (answers) => WINDOWS_REPLACED.includes(answers.windowsReplace)
      },
      {
        id: 'interiorDoors',
        type: EConfiguratorQuestionType.COUNTERS,
        summaryLabel: `${T}.interiorDoors.summary`,
        label: `${T}.interiorDoors.label`,
        help: `${T}.interiorDoors.help`,
        items: INTERIOR_DOOR_ITEMS,
        summaryAlwaysCount: true,
        allowCustom: true,
        customLabel: `${T}.interiorDoors.customLabel`
      },
      {
        id: 'entranceDoorNew',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.entranceDoorNew.summary`,
        label: `${T}.entranceDoorNew.label`,
        options: YES_NO_UNKNOWN_OPTIONS
      }
    ]
  },
  {
    id: 'exterior',
    icon: configuratorIcon('roof'),
    title: `${S}.exterior`,
    show: hasExteriorWork,
    questions: [
      {
        id: 'roofArea',
        type: EConfiguratorQuestionType.NUMBER,
        summaryLabel: `${T}.roofArea.summary`,
        label: `${T}.roofArea.label`,
        suffix: 'm²',
        step: 10,
        seed: 100,
        half: true
      },
      {
        id: 'roofAge',
        type: EConfiguratorQuestionType.CHIPS,
        summaryLabel: `${T}.roofAge.summary`,
        label: `${T}.roofAge.label`,
        options: ROOF_AGE_OPTIONS
      },
      {
        id: 'exteriorNote',
        type: EConfiguratorQuestionType.TEXTAREA,
        summaryLabel: `${T}.exteriorNote.summary`,
        label: `${T}.exteriorNote.label`,
        placeholder: `${T}.exteriorNote.placeholder`
      }
    ]
  },
  {
    id: 'planning',
    icon: configuratorIcon('layers'),
    title: `${S}.planning`,
    show: (answers) => !!answers.projectScope,
    questions: [
      {
        id: 'planningServices',
        type: EConfiguratorQuestionType.CHIPS,
        label: `${T}.planningServices.label`,
        help: `${T}.planningServices.help`,
        options: PLANNING_SERVICE_OPTIONS,
        multiple: true,
        exclusiveValues: [NONE_VALUE],
        allowCustom: true
      }
    ]
  }
];
